import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Download,
    Smartphone,
    ShieldCheck,
    CheckCircle2,
    ExternalLink,
    FileText,
    Info,
    ArrowDownCircle,
    RefreshCw
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';


/**
 * Converts standard Google Drive sharing links into direct-download URLs.
 * Uses drive.usercontent.google.com with confirm=t to bypass the virus-scan warning
 * for large files (>100MB like APKs).
 */
export const getDirectDownloadUrl = (url: string): string => {
    if (!url) return '';
    const trimmed = url.trim();

    // Check for Google Drive file ID
    const fileIdMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
        trimmed.match(/id=([a-zA-Z0-9_-]+)/);

    if (fileIdMatch && fileIdMatch[1]) {
        const fileId = fileIdMatch[1];
        // drive.usercontent.google.com with confirm=t bypasses the "too large for virus scan" warning page
        return `https://drive.usercontent.google.com/download?id=${fileId}&export=download&confirm=t`;
    }

    return trimmed;
};

const AnimalKartCrmPage: React.FC = () => {
    const [searchParams] = useSearchParams();

    // Dynamically loaded from Firestore app_settings/crm_app_config
    const [firestoreConfig, setFirestoreConfig] = useState<{
        appUrl?: string;
        androidVersion?: string;
        loading: boolean;
    }>({ loading: true });

    useEffect(() => {
        let isMounted = true;
        const fetchConfig = async () => {
            try {
                const docRef = doc(db, 'app_settings', 'crm_app_config');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && isMounted) {
                    const data = docSnap.data();
                    const appUrl = (data.app_url as string)?.trim() || '';
                    const androidVersion = (data.Android_lastest_version as string)?.trim() ||
                        (data.Android_latest_version as string)?.trim() ||
                        (data.latest_version as string)?.trim() || '';
                    setFirestoreConfig({
                        appUrl: appUrl || undefined,
                        androidVersion: androidVersion || undefined,
                        loading: false
                    });
                    return;
                }
            } catch (err) {
                console.error('Failed to load crm_app_config from Firestore:', err);
            }
            if (isMounted) {
                setFirestoreConfig(prev => ({ ...prev, loading: false }));
            }
        };

        fetchConfig();
        return () => { isMounted = false; };
    }, []);

    // Get drive/app URL from URL parameters > Firestore app_url
    const rawDriveUrl = useMemo(() => {
        const queryUrl = searchParams.get('url') ||
            searchParams.get('drive_url') ||
            searchParams.get('link');
        if (queryUrl) return decodeURIComponent(queryUrl);
        if (firestoreConfig.appUrl) return firestoreConfig.appUrl;
        return '';
    }, [searchParams, firestoreConfig.appUrl]);

    const directDownloadUrl = useMemo(() => {
        return rawDriveUrl ? getDirectDownloadUrl(rawDriveUrl) : '';
    }, [rawDriveUrl]);

    const hasDownloadUrl = Boolean(directDownloadUrl && !directDownloadUrl.includes('YOUR_GOOGLE_DRIVE_FILE_ID'));

    const [downloadTriggered, setDownloadTriggered] = useState(false);
    const [autoCountdown, setAutoCountdown] = useState(2);

    const triggerDownload = (url: string) => {
        if (!url || !hasDownloadUrl) return;
        setDownloadTriggered(true);
        window.location.assign(url);
    };

    // Auto-trigger the download shortly after page load once Firestore config is ready and URL exists
    useEffect(() => {
        if (firestoreConfig.loading || !hasDownloadUrl || !directDownloadUrl) return;

        const timer = setInterval(() => {
            setAutoCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    triggerDownload(directDownloadUrl);
                    return 0;
                }
                return prev - 1;
            });
        }, 800);

        return () => clearInterval(timer);
    }, [directDownloadUrl, hasDownloadUrl, firestoreConfig.loading]);



    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-col justify-between selection:bg-indigo-500 selection:text-white">


            {/* Main Content */}
            <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-12 flex flex-col items-center justify-center">
                {/* Hero Card */}
                <div className="w-full bg-slate-800/60 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-10 shadow-2xl shadow-indigo-950/50">

                    {/* App Icon & Header */}
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pb-8 border-b border-white/10">
                        <div className="w-24 h-24 rounded-2xl bg-gradient-to-tr from-indigo-600 to-violet-500 p-0.5 shadow-lg shadow-indigo-500/30 flex-shrink-0 flex items-center justify-center">
                            <div className="w-full h-full bg-slate-900/80 rounded-[14px] flex items-center justify-center">
                                <Smartphone className="w-12 h-12 text-indigo-400" />
                            </div>
                        </div>

                        <div className="text-center sm:text-left flex-1">
                            <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                                AnimalKart CRM for Android
                            </h1>

                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-3 text-xs text-slate-400">
                                <span className="inline-flex items-center gap-1.5 bg-indigo-500/10 text-indigo-300 px-2.5 py-1 rounded-md border border-indigo-500/20 font-medium">
                                    <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
                                    Verified & Secure APK
                                </span>
                                <span className="bg-white/5 px-2.5 py-1 rounded-md border border-white/5">
                                    Version: {firestoreConfig.androidVersion ? firestoreConfig.androidVersion : 'Latest'}
                                </span>
                                <span className="bg-white/5 px-2.5 py-1 rounded-md border border-white/5">Android 7.0+</span>
                                <span className="bg-white/5 px-2.5 py-1 rounded-md border border-white/5">File: APK</span>
                            </div>
                        </div>
                    </div>

                    {/* Auto-Download Status Banner */}
                    <div className="my-6">
                        {!hasDownloadUrl ? (
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex items-start gap-3">
                                <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <p className="font-semibold text-amber-300">APK link coming soon</p>
                                    <p className="text-amber-200/80 text-xs mt-0.5">
                                        The APK download URL has not been set in Firestore (<code className="bg-amber-950/60 px-1 py-0.5 rounded text-amber-300">app_settings/crm_app_config &rarr; app_url</code>) yet.
                                    </p>
                                </div>
                            </div>
                        ) : downloadTriggered ? (
                            <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-2xl p-4 flex items-center gap-3 animate-fade-in">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                                <p className="font-semibold text-emerald-300 text-sm">
                                    APK downloaded pls check downloads to install
                                </p>
                            </div>
                        ) : autoCountdown > 0 ? (
                            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin flex-shrink-0" />
                                    <div>
                                        <p className="font-semibold text-indigo-200 text-sm">Starting automatic download...</p>
                                        <p className="text-indigo-300/70 text-xs">Your download will begin in {autoCountdown} second{autoCountdown === 1 ? '' : 's'}.</p>
                                    </div>
                                </div>
                                <span className="text-xs font-mono font-bold bg-indigo-500/20 px-2 py-1 rounded text-indigo-300">
                                    {autoCountdown}s
                                </span>
                            </div>
                        ) : null}
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-2">
                        <a
                            href={hasDownloadUrl ? directDownloadUrl : '#'}
                            onClick={() => { if (hasDownloadUrl) setDownloadTriggered(true); }}
                            className={`w-full py-4 px-6 rounded-2xl font-bold flex items-center justify-center gap-3 text-base shadow-lg transition-all duration-200 ${!hasDownloadUrl
                                ? 'bg-slate-700 text-slate-400 cursor-not-allowed pointer-events-none'
                                : 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-violet-600 hover:from-indigo-400 hover:to-violet-500 text-white shadow-indigo-600/30 hover:shadow-indigo-600/50 hover:scale-[1.01] active:scale-[0.99]'
                                }`}
                        >
                            <Download className="w-5 h-5" />
                            <span>{hasDownloadUrl ? 'Download' : 'Link Coming Soon'}</span>
                        </a>
                    </div>

                    {/* Direct link fallback */}
                    {hasDownloadUrl && rawDriveUrl.includes('drive.google.com') && (
                        <div className="mt-4 text-center">
                            <a
                                href={rawDriveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors"
                            >
                                <ExternalLink className="w-3 h-3" />
                                Open file directly on Google Drive
                            </a>
                        </div>
                    )}
                </div>

                {/* Installation Steps */}
                <div className="w-full mt-10">
                    <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-indigo-400" />
                        How to Install AnimalKart CRM
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-slate-800/40 border border-white/5 rounded-2xl p-5 backdrop-blur-sm">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 font-bold flex items-center justify-center text-sm mb-3">
                                1
                            </div>
                            <h3 className="font-semibold text-white text-sm">Download the APK</h3>
                            <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                                Tap <strong>Download APK Now</strong> above. If your browser asks for confirmation, select <strong>Download anyway</strong>.
                            </p>
                        </div>

                        <div className="bg-slate-800/40 border border-white/5 rounded-2xl p-5 backdrop-blur-sm">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 font-bold flex items-center justify-center text-sm mb-3">
                                2
                            </div>
                            <h3 className="font-semibold text-white text-sm">Open the File</h3>
                            <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                                Once downloaded, pull down your notification tray and tap on the file, or locate it in your device's <strong>Downloads</strong> folder.
                            </p>
                        </div>

                        <div className="bg-slate-800/40 border border-white/5 rounded-2xl p-5 backdrop-blur-sm">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 font-bold flex items-center justify-center text-sm mb-3">
                                3
                            </div>
                            <h3 className="font-semibold text-white text-sm">Install & Launch</h3>
                            <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                                Tap <strong>Install</strong>. If prompted to allow installs from this source or browser, toggle <strong>Allow</strong> and finish installing.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-xs text-slate-400">
                    <div className="flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        <span>Official AnimalKart Release</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>100% Virus & Malware Free</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <ArrowDownCircle className="w-4 h-4 text-indigo-400" />
                        <span>Fast Direct CDN Download</span>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/10 py-6 px-4 text-center text-xs text-slate-500">
                <p>© {new Date().getFullYear()} AnimalKart CRM. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default AnimalKartCrmPage;
